import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild, OnInit, inject } from '@angular/core';
import { AlertService, PaginatorFetcher, PaginatorFilters, TableComponent, TableConfig } from '@michelin/theme';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Pagination } from '../../../product-data/models/response.model';
import { map } from 'rxjs';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { Store } from '@ngxs/store';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { SiteSearchDto } from '../../models/response.model';
import { SetupScreensService } from '../../services/setup-screens.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-site-setup-search',
  templateUrl: './site-setup-search.component.html',
  styleUrl: './site-setup-search.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SiteSetupSearchComponent implements OnInit {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<SiteSearchDto>;

  filterForm: FormGroup;
  filterFormBackend: FormGroup;
  sites: SiteSearchDto[] = [];
  siteCodes: string[] = [];
  siteNames: string[] = [];
  statusOptions: string[] = ['Active', 'Inactive'];
  plants: string[] = [];
  pagination: Pagination;
  pageSizeOptions = [10, 25, 50, 100];
  currentPage = 1;
  pageSize = 10;
  productSaveDisabled = true;

  tableConfig: TableConfig<SiteSearchDto> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    onRowClick: (elm: SiteSearchDto) => {
      this.router.navigateByUrl('/home/setup/siteSetup/' + elm.id);
    },
    columns: {}
  };

  fetcher: PaginatorFetcher<SiteSearchDto> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    this.generateFilterForm();
    return this.setupScreensService.getAllSites({ ...this.filterFormBackend.value, ...filters }, filters.page, filters.limit).pipe(
      map((response: any) => {
        this.sites = response.data.sites || [];
        this.pagination = response.data.pagination;
        return { items: this.sites, count: this.pagination.totalRecords };
      })
    );
  };

  public translationService = inject(TranslationService);
  public setupScreensService = inject(SetupScreensService);
  public featureFlagsService = inject(FeatureflagsService);
  public alertService = inject(AlertService);
  public fb = inject(FormBuilder);
  public store = inject(Store);
  public router = inject(Router);
  public cdr = inject(ChangeDetectorRef);

  constructor() {
    this.filterForm = this.fb.group({
      code: [[]],
      name: [[]],
      status: [[]]
    });
    this.filterFormBackend = this.fb.group({
      code: [[]],
      name: [[]],
      status: [[]]
    });
  }

  ngOnInit(): void {
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    this.filterForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(800)).subscribe(() => {
      this.generateFilterForm();
      this.tableRef?.patchExtraFilters(this.filterFormBackend.value, false);
    });
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.tableConfig.columns = {
        code: { cell: (row) => row.code, header: this.translationService.translate('codeText') },
        name: { cell: (row) => row.name, header: this.translationService.translate('nameText') },
        status: { cell: 'statusCell', header: this.translationService.translate('statusColumnText') }
      };
      this.cdr.markForCheck();
    });
  }

  clearFilters(): void {
    this.filterForm.reset({
      code: this.siteCodes,
      name: this.siteNames,
      status: []
    });
  }

  generateFilterForm(): void {
    const filterCode = this.filterForm.get('code').value;
    const filterName = this.filterForm.get('name').value;
    const filterStatus = this.filterForm.get('status').value;
    this.filterFormBackend.patchValue({
      code: filterCode.length > 0 && filterCode.length == this.siteCodes.length ? [] : filterCode,
      name: filterName.length > 0 && filterName.length == this.siteNames.length ? [] : filterName,
      status: filterStatus == 'Active' ? 'Y' : filterStatus == 'Inactive' ? 'N' : filterStatus
    });
  }

  createSite(): void {
    if (this.productSaveDisabled) {
      return;
    }
    this.router.navigateByUrl('/home/setup/siteSetup/create');
  }
}
